import { clearCache } from '@/composables/useCached'

// Offline-data-håndtering: rapportér hvor meget appen har gemt på enheden, og lad
// brugeren rydde det for at frigøre plads. Vi beholder den precachede app-shell
// (workbox-precache-cachen), så appen stadig kan åbnes offline efter en rydning —
// kun de tunge runtime-datacaches + de AI-tekst-caches i localStorage fjernes.

// Caches der hoerer til den indloggede bruger (/.auth/me + /api/user/*). Ryddes ved logout,
// saa naeste bruger paa samme enhed ikke kan se den forriges identitet og favoritter.
const USER_CACHES = ['api-user', 'auth']

// Runtime-caches oprettet af service-workeren (se src/sw.ts).
const DATA_CACHES = ['api-public', 'api-hero', 'osm-tiles', ...USER_CACHES]

export interface StorageUsage {
  bytes: number
  supported: boolean
}

// Samlet origin-forbrug (caches + IndexedDB m.m.). Efter en rydning falder tallet med
// den frigjorte plads, men rammer ikke nul — app-shellen bliver liggende. Det er ærligt:
// det er reelt hvad appen fylder på telefonen.
export async function estimateUsage(): Promise<StorageUsage> {
  const storage = navigator.storage as StorageManager | undefined
  if (!storage?.estimate) return { bytes: 0, supported: false }
  const { usage } = await storage.estimate()
  return { bytes: usage ?? 0, supported: true }
}

// De fire poster Indstillinger viser forbruget opdelt i. Det er ikke en opdeling opfundet
// til siden — det ER runtime-cachene fra src/sw.ts, så tallene kan ikke komme ud af trit
// med hvad der faktisk ligger på enheden.
export type StorageBucket = 'forecasts' | 'maps' | 'images' | 'pages'
export type StorageBuckets = Record<StorageBucket, number>

export interface StorageReport {
  bytes: number
  supported: boolean
  /** null når Cache Storage ikke findes — så har vi kun browserens samlede skøn. */
  buckets: StorageBuckets | null
}

function bucketFor(name: string): StorageBucket {
  // Hero før den generelle api-regel: 'api-hero' indeholder også 'api-'.
  if (name.includes('api-hero')) return 'images'
  if (name.includes('osm-tiles')) return 'maps'
  if (name.includes('api-') || name.includes('auth')) return 'forecasts'
  return 'pages' // workbox-precachen, altså selve app-shellen
}

async function cacheBytes(name: string): Promise<number> {
  const cache = await caches.open(name)
  const requests = await cache.keys()
  const sizes = await Promise.all(
    requests.map(async (req) => {
      const res = await cache.match(req)
      if (!res) return 0
      // content-length sparer os for at læse svaret. Den mangler for CORS-svar
      // (kortfliserne fra openstreetmap.org) medmindre serveren eksponerer den —
      // der er blobbens størrelse eneste vej.
      const len = Number(res.headers.get('content-length'))
      return Number.isFinite(len) && len > 0 ? len : (await res.blob()).size
    }),
  )
  return sizes.reduce((a, b) => a + b, 0)
}

// Forbruget pr. post, målt direkte i Cache Storage. Totalen er summen af posterne, så
// søjlen på siden går op — modsat estimateUsage(), der også tæller IndexedDB m.m. med og
// derfor ikke kan brydes ned.
export async function measureStorage(): Promise<StorageReport> {
  if (!('caches' in globalThis)) {
    const { bytes, supported } = await estimateUsage()
    return { bytes, supported, buckets: null }
  }
  const names = await caches.keys()
  // Måles færdigt først og lægges sammen bagefter: `buckets[b] += await …` læser den
  // gamle værdi før ventetiden, så to caches i samme post ville overskrive hinanden.
  const measured = await Promise.all(
    names.map(async (name) => [bucketFor(name), await cacheBytes(name)] as const),
  )
  const buckets: StorageBuckets = { forecasts: 0, maps: 0, images: 0, pages: 0 }
  for (const [bucket, bytes] of measured) buckets[bucket] += bytes
  return {
    bytes: buckets.forecasts + buckets.maps + buckets.images + buckets.pages,
    supported: true,
    buckets,
  }
}

// Slet runtime-datacaches + de klient-side AI-tekst-caches. Kalderne genlæser bagefter
// estimatet for at vise den frigjorte plads.
export async function clearOfflineData(): Promise<void> {
  await deleteCaches(DATA_CACHES)
  clearAiCache()
}

// Ryd kun det bruger-specifikke. De offentlige data (stedliste, hero-billeder, kort) kan
// roligt blive liggende, så appen stadig virker offline for den næste bruger.
export async function clearUserData(): Promise<void> {
  await deleteCaches(USER_CACHES)
  clearAiCache()
  // SWR-cachen lever i hukommelsen, ikke i Cache Storage, saa deleteCaches rammer den ikke.
  // Uden det her kunne en admin logge ud og den naeste bruger paa samme enhed se brugerlisten
  // og sigtmeldingerne i den fane, indtil den blev genindlaest.
  clearCache()
}

async function deleteCaches(names: string[]): Promise<void> {
  if (!('caches' in window)) return
  const existing = await caches.keys()
  await Promise.all(
    existing.filter((n) => names.some((d) => n.includes(d))).map((n) => caches.delete(n)),
  )
}

// AI-briefinger + trip-plan caches i localStorage (briefing:*, tripplan).
function clearAiCache(): void {
  try {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith('briefing:') || key === 'tripplan') localStorage.removeItem(key)
    }
  } catch {
    /* localStorage utilgængelig — ignorér */
  }
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const units = ['KB', 'MB', 'GB']
  let v = bytes / 1024
  let i = 0
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024
    i++
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`
}
